import {
  resolveB2CRates,
  isLegacyPricing,
  B2C_HOURLY_RATE_TIER_1,
  B2C_HOURLY_RATE_TIER_2,
  B2C_HOURLY_RATE_TIER_1_LAST_HOUR,
  LEGACY_B2C_MIN_HOURLY_RATE,
} from './constants';

export type Service =
  | 'CAREGIVING_WITH_UGOS_CAR'
  | 'CAREGIVING_WITH_USERS_CAR'
  | 'CAREGIVING_WITHOUT_CAR'
  | 'CAREGIVING_VIA_TELEMEDICINE'
  | 'ERRANDS'
  | 'PHONE_ASSISTANCE';

export const CITY_RADIUS_LIMIT_IN_KMS = 15; // km

// Re-exported for backwards compatibility — see ./constants for the
// historical-pricing-lock-aware resolveB2CRates().
export { CITY_MIN_DISTANCE, CITY_MIN_DISTANCE_DEFAULT } from './constants';

export function getHalfHourIntervalTimeCost(
  hours: number,
  pricePerHalfHour: number
) {
  return hours * pricePerHalfHour;
}

/**
 * Hours are rounded to the nearest 0.5h (https://app.clickup.com/t/1uq9v7n):
 * :00-:15 rounds down, :16-:45 rounds to ,5, :46-:00 rounds up.
 *
 * Current (post-cutoff): flat two-tier table (18€ hours 1-6, 16€ from hour
 * 7), `pricePerHour` is ignored (matches today's behavior).
 *
 * Legacy (pre-cutoff, true pre-refactor production formula — verified
 * against commit 4af7d5f^, a genuinely different formula, not just
 * different numbers): declining rate seeded by the caller's `pricePerHour`
 * (always 15 today), floored at LEGACY_B2C_MIN_HOURLY_RATE (8) —
 * `(idx+1>=8 ? 8 : pricePerHour-idx)` → 15,14,13,12,11,10,9,8,8,8...
 */
export function getHourlyIntervalTimeCost(
  hours: number,
  pricePerHour: number,
  reservationCreationDate?: Date
) {
  const legacy = isLegacyPricing(reservationCreationDate);
  const _hours = hours < 1 ? 1 : Math.round(hours * 2) / 2;
  const fullHours = Math.floor(_hours);
  const fractionalHour = _hours - fullHours;

  /**
   * Create an array of hour multipliers e.g. [1, 1, 0.5]
   * e.g.
   * hours = 2.55
   * hours gets rounded to 2.5
   * multiplier array = [1, 1, 0.5]
   */
  const hourMultiplierArray = Array.from({ length: fullHours }).map(
    (_, idx) => 1
  );

  if (fractionalHour) {
    hourMultiplierArray.push(fractionalHour);
  }

  /**
   * Multiply the hourly prices with the multiplier array,
   * so we correctly calculate the fractional hour
   *
   * multiplier array = [1, 1, 0.5]
   * hourly array = [15*1, 14*1, 13*0.5]
   */
  const hourlyArray = hourMultiplierArray.map((multiplier, idx) => {
    const rate = legacy
      ? Math.max(pricePerHour - idx, LEGACY_B2C_MIN_HOURLY_RATE)
      : idx + 1 <= B2C_HOURLY_RATE_TIER_1_LAST_HOUR
      ? B2C_HOURLY_RATE_TIER_1
      : B2C_HOURLY_RATE_TIER_2;
    return rate * multiplier;
  });

  // Return the reduced value
  return hourlyArray.reduce((a, b) => a + b, 0);
}

export function getDistanceCost(
  distance: number,
  minPrice = 5,
  reservationCreationDate?: Date
) {
  /**
   * @description Sets a price to be applied above 200km
   *  - It has been a different price prior to 15. 4. 2022
   *  - We're keeping the same logic as before but apply
   *  the same price as `below 200 km`; We're keeping this if we need to set
   *  a different price above 200km in the future again
   */
  const kmRate = resolveB2CRates(reservationCreationDate).B2C_KM_RATE;

  const numberOfAbove200Km = distance - 200 <= 0 ? 0 : distance - 200;
  const numberOfBelow200Km = numberOfAbove200Km > 0 ? 200 : distance;

  const above200KmCost = numberOfAbove200Km * kmRate;
  const below200KmCost = numberOfBelow200Km * kmRate;

  const cost = below200KmCost + above200KmCost;

  if (cost < minPrice) {
    return minPrice;
  }

  return cost;
}
